import { alpha } from '@mui/material/styles';
import React, { useState } from 'react';
import { Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Paper, Typography } from '@mui/material';
import _ from 'lodash';

// Helper to format the factor key into a readable label
const formatHeader = (key) => {
	if (key === 'interestExp') return 'Interest Expense';
	return _.startCase(key);
};
const formatNumberWithCommas = (value) => {
	// Case 1: The value is already a number. This is the fastest and most direct path.
	if (typeof value === 'number' && value !== null) {
		return value.toLocaleString('en-US');
	}

	// Case 2: The value is a string. We need to check if it represents a number.
	if (typeof value === 'string') {
		// This regex checks if the entire string is a valid integer or decimal number.
		// - `^`         : Start of the string
		// - `-?`        : Optional negative sign
		// - `\d+`       : One or more digits
		// - `(\.\d+)?`  : An optional decimal part
		// - `$`         : End of the string
		// This will match "123", "123.45", "-500", but NOT "2025-07-11" or "123 Main St".
		const isNumericString = /^-?\d+(\.\d+)?$/.test(value.trim());

		if (isNumericString) {
			// If it's a numeric string, convert it to a number and then format it.
			return parseFloat(value).toLocaleString('en-US');
		}
	}

	// Case 3: The value is null, an object, or a non-numeric string (like a date).
	// Return it as-is.
	return value;
};
const ALL_DATA_COLUMNS = [
	'date',
	'period',
	'grossRent',
	'advanceAmount',
	'netPay',
	'ibr',
	'discountFactor',
	'interestExp',
	'payment',
	'openLeaseLiability',
	'closingLeaseLiability',
	'adjustment',
];

// Reusable styles for cells
const groupCellStyle = {
	verticalAlign: 'top',
	borderRight: '1px solid #ddd',
	fontWeight: 'bold',
	backgroundColor: '#fff',
	color: 'text.primary',
	pt: 2,
};

export const ComparisonTable = ({ groupedData, factor, onRowClick }) => {
	const [hoveredRow, setHoveredRow] = useState(null);

	const columns = ALL_DATA_COLUMNS.filter((col) => col !== factor && col !== 'leaseTitle');

	// [MODIFIED] We now use TableContainer to handle scrolling.
	// The outer border is applied here.
	return (
		<TableContainer component={Paper} elevation={0} sx={{ border: '1px solid', borderColor: 'divider', maxHeight: '75vh', borderRadius: '8px' }}>
			<Table
				stickyHeader
				sx={{
					minWidth: 1200,
					// --- MODERNIZED BORDER APPROACH ---
					// Use the theme's divider color for a softer look
					'& .MuiTableCell-root': {
						borderRight: '1px solid',
						borderColor: 'divider',
						whiteSpace: 'nowrap',
					},
					'& .MuiTableCell-root:last-of-type': {
						borderRight: 0,
					},
					// --- MODERNIZED BODY FONT ---
					// Set a base style for body cells
					'& .MuiTableBody-root .MuiTableCell-root': {
						fontSize: '0.875rem', // 14px
						color: 'text.secondary',
					},
				}}
				aria-label="modern sticky comparison table"
			>
				<TableHead>
					<TableRow
						sx={{
							// --- COOL & MODERN HEADER STYLING ---
							'& .MuiTableCell-root': {
								backgroundColor: 'grey.100', // A neutral, clean background
								fontWeight: 600,
								color: 'text.primary',
								textTransform: 'uppercase', // Professional look
								letterSpacing: '0.5px',
								fontSize: '0.75rem', // 12px, common for modern headers
								borderBottom: '2px solid',
								borderColor: 'divider',
							},
						}}
					>
						{/* No need for Typography variant="h5", style the cell directly */}
						<TableCell>{formatHeader(factor)}</TableCell>
						<TableCell>Lease Title</TableCell>
						{columns.map((col) => (
							<TableCell key={col} align="center">
								{formatHeader(col)}
							</TableCell>
						))}
					</TableRow>
				</TableHead>

				{groupedData.map((group) => {
					const primaryRowSpan = _.sumBy(group.leases, (lease) => lease.items.length);

					return (
						<TableBody
							key={group.groupValue}
							// A slightly thicker, darker border to separate major groups
							sx={{ '& tr:last-child': { '& > td': { borderBottom: '2px solid #e0e0e0' } } }}
						>
							{group.leases.map((lease, leaseIndex) => (
								<React.Fragment key={lease.leaseTitle}>
									{lease.items.map((item, itemIndex) => {
										const rowId = `${item.leaseId}-${item.period}`;

										return (
											<TableRow
												key={rowId}
												onMouseEnter={() => setHoveredRow(rowId)}
												onMouseLeave={() => setHoveredRow(null)}
												sx={{
													// --- AESTHETIC HOVER EFFECT ---
													'&:hover': {
														backgroundColor: (theme) => alpha(theme.palette.primary.main, 0.04),
													},
												}}
											>
												{/* Primary Group Cell */}
												{leaseIndex === 0 && itemIndex === 0 && (
													<TableCell rowSpan={primaryRowSpan} sx={{ ...groupCellStyle, fontSize: '1rem' }}>
														{/* Using Typography here to give it more weight */}
														<Typography variant="subtitle1" fontWeight="600">
															{formatNumberWithCommas(group.groupValue)}
														</Typography>
													</TableCell>
												)}

												{/* Secondary Group Cell (Lease Title) */}
												{itemIndex === 0 && (
													<TableCell rowSpan={lease.items.length} sx={groupCellStyle}>
														{lease.leaseTitle}
													</TableCell>
												)}

												{/* Data Cells */}
												{columns.map((colKey) => (
													<TableCell
														key={colKey}
														align="right"
														sx={{
															cursor: 'pointer',
															// Smooth transition for all properties
															transition: 'background-color 0.2s ease-in-out',
															fontVariantNumeric: 'tabular-nums',
														}}
														onClick={() => onRowClick(item)}
													>
														{/* Format numbers for a cleaner look if possible */}
														{formatNumberWithCommas(_.get(item, colKey, '–'))}
													</TableCell>
												))}
											</TableRow>
										);
									})}
								</React.Fragment>
							))}
						</TableBody>
					);
				})}
			</Table>
		</TableContainer>
	);
};
